import { reactive } from 'vue'
import {
  cancelFactoryRun,
  createFactoryRun,
  type FactoryRunActionResponse,
  getFactoryRun,
  listFactoryRunsForFactory,
  listFactoryStepRuns,
  pauseFactoryRun,
  resumeFactoryRun,
  subscribeFactoryRun
} from '@/utils/api'
import { isApiError } from '@/types/api'
import type { FactoryRunRecord, FactoryStepRunRecord } from '@/types/factories'
import type { ToolEvent } from '@/types/msq'
import type { Store } from 'vuex'

export interface FactoryStreamState {
  status: 'idle' | 'queued' | 'running' | 'paused' | 'completed' | 'error' | 'cancelled'
  runId: string | null
  run: FactoryRunRecord | null
  stepRuns: FactoryStepRunRecord[]
  error: string | null
}

export interface FactoryStreamActionResult {
  success: boolean
  noop?: boolean
  message?: string
  run?: FactoryRunRecord | null
  error?: string
}

export function useFactoryStream(appStore: Store<unknown>) {
  const state = reactive<FactoryStreamState>({
    status: 'idle',
    runId: null,
    run: null,
    stepRuns: [],
    error: null
  })

  let runStreamUnsubscribe: (() => void) | null = null
  let activeFactoryId: string | null = null
  const pendingAgentDeltasByStepRunId = new Map<string, string>()
  const pendingAgentReplacementsByStepRunId = new Map<string, string>()
  const pendingToolEventsByStepRunId = new Map<string, ToolEvent[]>()

  function getStorageKey(factoryId: string): string {
    return `msq.factory.run.${factoryId}`
  }

  function showSnackbar(text: string, color: string = 'info'): void {
    void appStore.dispatch('updateSnackbar', { text, color })
  }

  function addLogEvent(title: string, content?: object | string): void {
    void appStore.dispatch('addLogEvent', { title, content })
  }

  function syncRunRecord(record: FactoryRunRecord): void {
    appStore.commit('mergeFactoryRun', record)
  }

  function syncStepRuns(runId: string, stepRuns: FactoryStepRunRecord[]): void {
    appStore.commit('setFactoryStepRuns', { runId, stepRuns, append: false })
  }

  function isTerminalStatus(status: FactoryRunRecord['status']): boolean {
    return status === 'completed' || status === 'error' || status === 'cancelled'
  }

  function clearStoredRunId(runId: string): void {
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i)
      if (key?.startsWith('msq.factory.run.')) {
        try {
          if (sessionStorage.getItem(key) === runId) {
            sessionStorage.removeItem(key)
          }
        } catch {
          /* ignore */
        }
      }
    }
  }

  function reset(): void {
    state.status = 'idle'
    state.runId = null
    state.run = null
    state.stepRuns = []
    state.error = null
    if (runStreamUnsubscribe) {
      runStreamUnsubscribe()
      runStreamUnsubscribe = null
    }
    pendingAgentDeltasByStepRunId.clear()
    pendingAgentReplacementsByStepRunId.clear()
    pendingToolEventsByStepRunId.clear()
  }

  function applySnapshot(record: FactoryRunRecord): void {
    state.runId = record.runId
    state.run = record
    state.status = record.status
    state.error =
      record.status === 'error'
        ? record.errorMessage || 'Factory run failed'
        : record.status === 'cancelled'
          ? 'Factory run was cancelled'
          : null
    syncRunRecord(record)
  }

  function patchRunRecord(patch: Partial<FactoryRunRecord>): void {
    if (!state.run) return
    applySnapshot({ ...state.run, ...patch })
  }

  function syncSingleStepRun(stepRun: FactoryStepRunRecord): void {
    appStore.commit('mergeFactoryStepRun', stepRun)
  }

  function sortStepRuns(): void {
    state.stepRuns.sort((a, b) => a.sequence - b.sequence)
  }

  function isStepTerminal(status: FactoryStepRunRecord['status']): boolean {
    return status === 'completed' || status === 'error' || status === 'cancelled' || status === 'skipped'
  }

  function isToolEvent(value: unknown): value is ToolEvent {
    if (!value || typeof value !== 'object') return false
    const record = value as Record<string, unknown>
    return typeof record.type === 'string'
  }

  function isFactoryStepRunRecord(value: unknown): value is FactoryStepRunRecord {
    if (!value || typeof value !== 'object') return false
    const record = value as Record<string, unknown>
    return (
      typeof record.stepRunId === 'string' &&
      typeof record.runId === 'string' &&
      typeof record.ownerUserId === 'string' &&
      typeof record.factoryConfigId === 'string' &&
      typeof record.stepId === 'string' &&
      typeof record.stepIndex === 'number' &&
      typeof record.stepName === 'string' &&
      typeof record.sequence === 'number' &&
      (record.kind === 'agent' || record.kind === 'workflow') &&
      typeof record.invoked === 'object' &&
      record.invoked !== null &&
      (record.triggeredBy === 'manual_start' ||
        record.triggeredBy === 'previous_step_output' ||
        record.triggeredBy === 'loopback' ||
        record.triggeredBy === 'scheduler') &&
      (typeof record.upstreamStepRunId === 'string' || record.upstreamStepRunId === null) &&
      typeof record.input === 'string' &&
      typeof record.output === 'string' &&
      (typeof record.workflowDataPayloadIn === 'string' || record.workflowDataPayloadIn === null) &&
      (record.status === 'queued' ||
        record.status === 'running' ||
        record.status === 'completed' ||
        record.status === 'error' ||
        record.status === 'cancelled' ||
        record.status === 'skipped') &&
      Array.isArray(record.toolEvents) &&
      typeof record.startedAt === 'number' &&
      typeof record.createdAt === 'number' &&
      typeof record.updatedAt === 'number'
    )
  }

  function applyPendingLiveData(stepRunId: string): boolean {
    const index = state.stepRuns.findIndex((entry) => entry.stepRunId === stepRunId)
    if (index === -1) return false

    const current = state.stepRuns[index]
    const pendingDelta = pendingAgentDeltasByStepRunId.get(stepRunId)
    const pendingReplacement = pendingAgentReplacementsByStepRunId.get(stepRunId)
    const pendingToolEvents = pendingToolEventsByStepRunId.get(stepRunId)
    const patch: Partial<FactoryStepRunRecord> = {}

    if (pendingReplacement && current.status === 'running') {
      patch.output = `${pendingReplacement}${pendingDelta || ''}`
    } else if (pendingDelta && current.status === 'running') {
      patch.output = `${current.output || ''}${pendingDelta}`
    }

    if (pendingToolEvents?.length && current.status === 'running') {
      patch.toolEvents = [...(current.toolEvents || []), ...pendingToolEvents]
    }

    if (Object.keys(patch).length > 0) {
      pendingAgentDeltasByStepRunId.delete(stepRunId)
      pendingAgentReplacementsByStepRunId.delete(stepRunId)
      pendingToolEventsByStepRunId.delete(stepRunId)
      state.stepRuns[index] = { ...current, ...patch }
      syncSingleStepRun(state.stepRuns[index])
      return true
    }

    if (isStepTerminal(current.status)) {
      pendingAgentDeltasByStepRunId.delete(stepRunId)
      pendingAgentReplacementsByStepRunId.delete(stepRunId)
      pendingToolEventsByStepRunId.delete(stepRunId)
    }
    return false
  }

  function upsertStepRun(stepRun: FactoryStepRunRecord): void {
    const index = state.stepRuns.findIndex((entry) => entry.stepRunId === stepRun.stepRunId)
    let upsertedStepRun: FactoryStepRunRecord
    if (index === -1) {
      upsertedStepRun = stepRun
      state.stepRuns.push(upsertedStepRun)
      sortStepRuns()
    } else {
      upsertedStepRun = { ...state.stepRuns[index], ...stepRun }
      state.stepRuns[index] = upsertedStepRun
    }
    const appliedPendingData = applyPendingLiveData(stepRun.stepRunId)
    if (!appliedPendingData) {
      syncSingleStepRun(upsertedStepRun)
    }
  }

  function mergeStepRun(stepRunId: string, patch: Partial<FactoryStepRunRecord>): boolean {
    const index = state.stepRuns.findIndex((entry) => entry.stepRunId === stepRunId)
    if (index === -1) return false
    state.stepRuns[index] = { ...state.stepRuns[index], ...patch }
    syncSingleStepRun(state.stepRuns[index])
    return true
  }

  function appendPendingAgentDelta(stepRunId: string, delta: string): void {
    pendingAgentDeltasByStepRunId.set(
      stepRunId,
      `${pendingAgentDeltasByStepRunId.get(stepRunId) || ''}${delta}`
    )
  }

  function appendPendingToolEvent(stepRunId: string, event: ToolEvent): void {
    pendingToolEventsByStepRunId.set(stepRunId, [
      ...(pendingToolEventsByStepRunId.get(stepRunId) || []),
      event
    ])
  }

  async function hydrateSteps(runId: string): Promise<void> {
    const response = await listFactoryStepRuns(runId, 200, 0)
    if (isApiError(response) || !response.success) return
    const liveStepRunsById = new Map(state.stepRuns.map((stepRun) => [stepRun.stepRunId, stepRun]))
    state.stepRuns = response.data
      .map((stepRun) => {
        const live = liveStepRunsById.get(stepRun.stepRunId)
        if (!live || stepRun.status !== 'running') {
          return stepRun
        }
        return {
          ...stepRun,
          output: (live.output || '').length > (stepRun.output || '').length ? live.output : stepRun.output,
          toolEvents: (live.toolEvents || []).length > (stepRun.toolEvents || []).length
            ? live.toolEvents
            : stepRun.toolEvents
        }
      })
      .sort((a, b) => a.sequence - b.sequence)
    for (const stepRun of state.stepRuns) {
      applyPendingLiveData(stepRun.stepRunId)
    }
    syncStepRuns(runId, state.stepRuns)
  }

  function handleEvent(event: any): void {
    switch (event.type) {
      case 'run_started':
        state.status = 'running'
        patchRunRecord({ status: 'running', updatedAt: Date.now() })
        break
      case 'run_paused':
        state.status = 'paused'
        patchRunRecord({ status: 'paused', pausedAt: Date.now(), updatedAt: Date.now() })
        break
      case 'run_cancelled':
        state.status = 'cancelled'
        state.error = 'Factory run was cancelled'
        patchRunRecord({
          status: 'cancelled',
          cancelledAt: Date.now(),
          updatedAt: Date.now(),
          errorMessage: state.run?.errorMessage || 'user_cancelled'
        })
        break
      case 'run_completed':
      case 'run_completed_at_cap':
      case 'run_completed_step_cap':
        state.status = 'completed'
        patchRunRecord({
          status: 'completed',
          completedAt: Date.now(),
          updatedAt: Date.now(),
          aggregateUsage: event.aggregateUsage || state.run?.aggregateUsage || null
        })
        break
      case 'run_error':
      case 'error':
        state.status = 'error'
        state.error = event.message || 'Factory execution failed'
        patchRunRecord({
          status: 'error',
          updatedAt: Date.now(),
          errorMessage: event.message || state.run?.errorMessage || 'Factory execution failed'
        })
        break
      case 'step_started':
        if (isFactoryStepRunRecord(event.stepRun)) {
          upsertStepRun(event.stepRun)
        }
        if (state.runId) {
          // Optimistic upsert makes the step visible immediately; hydration then reconciles authoritative fields.
          void hydrateSteps(state.runId)
        }
        break
      case 'step_agent_delta':
        if (typeof event.stepRunId === 'string' && typeof event.delta === 'string') {
          const current = state.stepRuns.find((entry) => entry.stepRunId === event.stepRunId)
          if (current && isStepTerminal(current.status)) {
            break
          }
          const merged = mergeStepRun(event.stepRunId, {
            output: `${current?.output || ''}${event.delta}`,
            status: 'running'
          })
          if (!merged) {
            appendPendingAgentDelta(event.stepRunId, event.delta)
          }
        }
        break
      case 'step_agent_replace':
        if (typeof event.stepRunId === 'string' && typeof event.content === 'string') {
          const current = state.stepRuns.find((entry) => entry.stepRunId === event.stepRunId)
          if (current && isStepTerminal(current.status)) {
            break
          }
          const merged = mergeStepRun(event.stepRunId, {
            output: event.content,
            status: 'running'
          })
          if (!merged) {
            // Replacement events are authoritative snapshots; if multiple arrive before hydration,
            // keep the latest replacement and append any later queued deltas when the row appears.
            pendingAgentReplacementsByStepRunId.set(event.stepRunId, event.content)
          }
        }
        break
      case 'step_agent_tool_event':
        if (typeof event.stepRunId === 'string' && event.event) {
          if (!isToolEvent(event.event)) {
            break
          }
          const toolEvent = event.event
          const current = state.stepRuns.find((entry) => entry.stepRunId === event.stepRunId)
          if (current && isStepTerminal(current.status)) {
            break
          }
          const merged = mergeStepRun(event.stepRunId, {
            toolEvents: [...(current?.toolEvents || []), toolEvent],
            status: 'running'
          })
          if (!merged) {
            appendPendingToolEvent(event.stepRunId, toolEvent)
          }
        }
        break
      case 'step_completed':
      case 'step_error':
      case 'step_validation_attempt':
      case 'step_workflow_completed':
        if (state.runId) {
          void hydrateSteps(state.runId)
        }
        break
      default:
        break
    }
  }

  async function refreshRun(runId: string): Promise<void> {
    const response = await getFactoryRun(runId)
    if (isApiError(response) || !response.success) return
    applySnapshot(response.data)
    await hydrateSteps(runId)
  }

  async function subscribeToRun(runId: string): Promise<void> {
    if (runStreamUnsubscribe) {
      runStreamUnsubscribe()
      runStreamUnsubscribe = null
    }

    runStreamUnsubscribe = subscribeFactoryRun(runId, {
      onSnapshot: (record) => {
        applySnapshot(record)
        void hydrateSteps(record.runId)
      },
      onEvent: (event) => {
        handleEvent(event)
      },
      onDone: () => {
        if (state.runId) {
          void refreshRun(state.runId)
        }
        runStreamUnsubscribe = null
      },
      onError: (message) => {
        state.error = message
        state.status = 'error'
        addLogEvent('Factory Run Stream Error', message)
        runStreamUnsubscribe = null
      }
    })
  }

  function normalizeRunActionResponse(
    response: FactoryRunActionResponse,
    title: string
  ): FactoryStreamActionResult {
    applySnapshot(response.data)
    if (response.noop && response.message) {
      showSnackbar(response.message, 'info')
      addLogEvent(title, response.message)
    }
    return {
      success: true,
      noop: response.noop,
      message: response.message,
      run: response.data
    }
  }

  async function execute(factoryConfigId: string, initialCarryPayload?: string): Promise<FactoryStreamActionResult> {
    reset()
    activeFactoryId = factoryConfigId
    state.status = 'queued'

    const response = await createFactoryRun(factoryConfigId, initialCarryPayload)
    if (isApiError(response) || !response.success) {
      const message = isApiError(response) ? response.message : 'Failed to start factory run'
      state.status = 'error'
      state.error = message
      state.run = null
      showSnackbar(`Factory Run Error: ${message}`, 'error')
      addLogEvent('Factory Run Error', response)
      return { success: false, error: message, run: null }
    }

    state.runId = response.runId
    try { sessionStorage.setItem(getStorageKey(factoryConfigId), response.runId) } catch { /* ignore */ }
    applySnapshot(response.data)
    await hydrateSteps(response.runId)
    await subscribeToRun(response.runId)
    return { success: true, run: response.data }
  }

  async function pause(): Promise<FactoryStreamActionResult> {
    if (!state.runId) {
      const message = 'No active factory run to pause.'
      showSnackbar(message, 'error')
      return { success: false, error: message, run: state.run }
    }
    const response = await pauseFactoryRun(state.runId)
    if (isApiError(response) || !response.success) {
      const message = isApiError(response) ? response.message : 'Failed to pause factory run'
      showSnackbar(`Factory Pause Error: ${message}`, 'error')
      addLogEvent('Factory Pause Error', response)
      return { success: false, error: message, run: state.run }
    }
    return normalizeRunActionResponse(response, 'Factory Pause')
  }

  async function resumeRun(): Promise<FactoryStreamActionResult> {
    if (!state.runId) {
      const message = 'No factory run is available to resume.'
      showSnackbar(message, 'error')
      return { success: false, error: message, run: state.run }
    }
    const response = await resumeFactoryRun(state.runId)
    if (isApiError(response) || !response.success) {
      const message = isApiError(response) ? response.message : 'Failed to resume factory run'
      showSnackbar(`Factory Resume Error: ${message}`, 'error')
      addLogEvent('Factory Resume Error', response)
      return { success: false, error: message, run: state.run }
    }

    const result = normalizeRunActionResponse(response, 'Factory Resume')
    if (response.data.status === 'running') {
      await subscribeToRun(response.data.runId)
    }
    return result
  }

  async function cancel(): Promise<FactoryStreamActionResult> {
    if (!state.runId) {
      const message = 'No factory run is available to cancel.'
      showSnackbar(message, 'error')
      return { success: false, error: message, run: state.run }
    }

    const activeRunId = state.runId
    const response = await cancelFactoryRun(activeRunId)
    if (isApiError(response) || !response.success) {
      const message = isApiError(response) ? response.message : 'Failed to cancel factory run'
      showSnackbar(`Factory Cancel Error: ${message}`, 'error')
      addLogEvent('Factory Cancel Error', response)
      return { success: false, error: message, run: state.run }
    }

    clearStoredRunId(activeRunId)
    if (runStreamUnsubscribe) {
      runStreamUnsubscribe()
      runStreamUnsubscribe = null
    }

    state.error = 'Factory run was cancelled'
    state.status = 'cancelled'
    if (state.run) {
      patchRunRecord({
        status: 'cancelled',
        cancelledAt: Date.now(),
        updatedAt: Date.now(),
        errorMessage: 'user_cancelled'
      })
    }

    await refreshRun(activeRunId)

    return {
      success: true,
      message: response.alreadyCancelled ? 'Factory run was already cancelled.' : 'Factory run cancelled.',
      run: state.run
    }
  }

  async function resume(factoryId: string, options: { onlyActive?: boolean } = {}): Promise<boolean> {
    let storedRunId: string | null = null
    try { storedRunId = sessionStorage.getItem(getStorageKey(factoryId)) } catch { /* ignore */ }
    activeFactoryId = factoryId

    let record: FactoryRunRecord | null = null
    if (storedRunId) {
      const response = await getFactoryRun(storedRunId)
      if (!isApiError(response) && response.success) {
        record = response.data
      } else {
        clearStoredRunId(storedRunId)
      }
    }

    if (!record) {
      const listResponse = await listFactoryRunsForFactory(factoryId, 1, 0)
      if (isApiError(listResponse) || !listResponse.success || !listResponse.data.length) {
        return false
      }
      record = listResponse.data[0]
      try { sessionStorage.setItem(getStorageKey(factoryId), record.runId) } catch { /* ignore */ }
    }

    const isTerminal = isTerminalStatus(record.status)
    if (options.onlyActive && isTerminal) {
      clearStoredRunId(record.runId)
      return false
    }

    applySnapshot(record)
    await hydrateSteps(record.runId)

    if (record.status === 'paused') {
      return true
    }

    if (isTerminal) {
      return true
    }

    await subscribeToRun(record.runId)
    return true
  }

  return {
    state,
    execute,
    pause,
    resumeRun,
    cancel,
    reset,
    resume,
    get activeFactoryId() {
      return activeFactoryId
    }
  }
}
